import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { __GetContentTypeConfigNew } from "../getters/gettersV2";

import { useBannerContext } from "oolib";
import { executeInQueue } from "../executeInQueue";
import { useGenErrorMsgBanners } from "../useGenErrorMsgBanners";

import { useGetQueryData } from "./general";
import { parseSpecialSyntax } from "../parseSpecialSyntax";
import { parseSpecialConfigSyntax } from "@okf/ootils";

export const updateRolesConfigCacheForNewlyCreatedTpl = ({
  updatedRolesConfig,
  queryClient,
}) => {
  if (!updatedRolesConfig) return;

  //else
  queryClient.setQueryData(["platformConfigs"], (oldData) => {
    if (!oldData) return oldData;

    return {
      ...oldData,
      _Roles: updatedRolesConfig.data,
    };
  });
};

/**
 * this is what this function does if the whitelist array has all 4 options in it:
 *
 * create/update [tpl <contentType>]
 * create/update [tci tpl <contentType>]
 *
 * update [tci tpls] if exists
 * update [allTpls] if exists BUT this always exists
 */
export const updateAllTplsCache = ({
  queryClient,
  updatedTpl,
  whitelist = [["allTpls"], ["tci", "tpls"], ["tci", "tpl"], ["tpl"]],
}) => {
  console.log("direct import", updatedTpl?.kp_content_type);
  if (!updatedTpl) return;

  const contentType = updatedTpl?.kp_content_type;

  const updateTplInArray = (queryKey) => {
    /**
     * we do this existing data check only for tci tpls specifically,
     * cuz. thats the only one that may potentially not exist and we
     * dont wanna mess with that, cuz issues may happen
     */
    const existingData = queryClient.getQueryData(queryKey);
    if (existingData === undefined) {
      console.log(
        `Cache key ${JSON.stringify(queryKey)} doesn't exist, skipping update`
      );
      return;
    }

    queryClient.setQueryData(queryKey, (oldData) => {
      if (!oldData || !Array.isArray(oldData)) return oldData;

      // Check if template already exists
      const existingIndex = oldData.findIndex(
        (tpl) => tpl.kp_content_type === contentType
      );

      if (existingIndex !== -1) {
        // Update existing template
        return oldData.map((tpl) =>
          tpl.kp_content_type === contentType ? updatedTpl : tpl
        );
      } else {
        // Add new template to array
        return [...oldData, updatedTpl];
      }
    });
  };

  const isWhitelisted = (key) =>
    whitelist.some(
      (whitelistKey) =>
        whitelistKey.length === key.length &&
        whitelistKey.every((str, index) => str === key[index])
    );

  // Update individual tpl caches
  if (isWhitelisted(["tpl"])) {
    queryClient.setQueryData(["tpl", contentType], updatedTpl);
  }

  if (isWhitelisted(["tci", "tpl"])) {
    queryClient.setQueryData(["tci", "tpl", contentType], updatedTpl);
  }

  // Update array caches
  if (isWhitelisted(["allTpls"])) {
    updateTplInArray(["allTpls"]);
  }

  if (isWhitelisted(["tci", "tpls"])) {
    updateTplInArray(["tci", "tpls"]);
  }
};

export const useGetOnePublishedOrUnpublishedTpl = ({
  contentType,
  enabled,
}) => {
  return useQuery(
    ["tci", "tpl", contentType],
    () => {
      return axios
        .get(`/api/tpl/getTpl/${contentType}?fetchFromDB=true`)
        .then((res) => res.data[0]);
    },
    {
      enabled,
    }
  );
};

//only used in the tci context
export const useGetAllPublishedAndUnpublishedTpls = () => {
  /**
   * tplId is called kp_content_type in the tpl config.
   * should rename eventually to tplId
   */
  const queryClient = useQueryClient();

  return useQuery(["tci", "tpls"], () =>
    axios.get(`/api/tpl/getAllTpls?fetchFromDB=true`).then((res) => res.data)
  );
};

export const useGetTplForEdit = (contentType, options = {}) => {
  return useQuery(
    ["tci", "tplForEdit", contentType],
    () =>
      axios
        .get(`/api/tpl/getTplForEdit/${contentType}`)
        .then((res) => res.data),
    {
      enabled: options?.enabled,
      cacheTime: 0,
      /**
       * we dont ever wanna cache this, because everytime a tpl is
       * opened/closed we want to fetch it afresh, since the previous
       * time it was opened, changes could have been made to it,
       * which we dont wanna hassle ourselves by updating this query's cache
       */
    }
  );
};

export const useCreateTpl = () => {
  const queryClient = useQueryClient();
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  return useMutation(
    ({
      kp_content_type,
      category,
      tpl,
      content,
      action,
      customBlocksInjectConfig,
    }) =>
      axios.post(
        `/api/tpl/createTpl/${category}/${kp_content_type}`,
        {
          tpl,
          content,
          customBlocksInjectConfig,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            action,
          },
        }
      ),
    {
      onSuccess: (res, { kp_content_type, action }) => {
        queryClient.invalidateQueries(["tci", "tpls"]);
        if (action === "CREATE_AND_PUBLISH") {
          /**
           * if template has been published,
           * then we want to update allTpls & tpl <contentType>
           * cache obviously.
           *
           * allTpls would already exist in cache cuz we fetch it on
           * app load
           *
           * tpl <contentType> cache will not exist
           */
          updateAllTplsCache({
            queryClient,
            updatedTpl: res?.data?.tpl,
            whitelist: [["allTpls"], ["tpl"]],
          });
        }

        /**
         * irrespective of create or create & publish.
         * we can keep the tci, tpls & tci tpl <contentType>
         * cache uptodate.
         *
         * tci tpl <contentType> may not exist and it will get
         * created in that case which wont be a problem
         *
         * tci tpls may not exist and it wont get created
         * in that case cuz it could lead to issues
         *
         */
        updateAllTplsCache({
          queryClient,
          updatedTpl: res?.data?.tpl,
          whitelist: [
            ["tci", "tpls"],
            ["tci", "tpl"],
          ],
        });

        /**
         * we also obviously need to update cache for this newly created tpl
         * (ideally especially in the case of CREATE_AND_PUBLISH)
         * but no harm doing it for both cases.
         */
        updateRolesConfigCacheForNewlyCreatedTpl({
          updatedRolesConfig: res?.data?.updatedRolesConfig,
          queryClient,
        });
      },
      onError: (err) => {
        genErrorMsgBanners({ err });
      },
    }
  );
};

const createMultipleTpls = async (queryPayloads) => {
  const results = await executeInQueue({
    dataAry: queryPayloads,
    callback: async ({
      kp_content_type,
      category,
      tpl,
      content,
      action,
      customBlocksInjectConfig,
    }) => {
      try {
        const result = await axios.post(
          `/api/tpl/createTpl/${category}/${kp_content_type}`,
          {
            tpl,
            content,
            customBlocksInjectConfig,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
            params: {
              action,
            },
          }
        );
        return { status: "success", result };
      } catch (err) {
        return { status: "error", err };
      }
    },
  });
  console.log({ results });
  if (results.some((r) => r.status === "error"))
    throw { errors: [{ msg: "Some tpls were not created" }] };
  return results;
};


export const useForceUpdateTpl = () => {
  const queryClient = useQueryClient();
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  return useMutation(
    (body) =>
      axios.patch("/api/tpl/forceUpdateTpl", body, {
        headers: {
          "Content-Type": "application/json",
        },
      }),
    {
      onSuccess: (res) => {
        /**
         * i dont think this is ever used from the frontend
         * but this is like making changes and publishing a tpl
         *
         * which will
         *
         * create/update tpl <contentType>
         * create/update tci tpl <contentType>
         *
         * update tci tpls if exists
         * update allTpls if exists BUT this always exists
         */
        updateAllTplsCache({
          queryClient,
          updatedTpl: res?.data,
        });
      },
      onError: (err) => {
        genErrorMsgBanners({ err });
      },
    }
  );
};

export const useSaveTplChanges = () => {
  const queryClient = useQueryClient();
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  return useMutation(
    ({ tplConfig, contentType: _contentType }) => {
      const contentType = _contentType || tplConfig.kp_content_type;
      return axios.patch(`/api/tpl/updateTpl/${contentType}`, tplConfig, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    {
      onSuccess: (res, { type }) => {
        /**
         * we just update the tci related caches here, just so that
         * in case user exists or presses browser back and exists, the
         * latest changes are available in the tci related caches. which
         * is what we want
         */
        updateAllTplsCache({
          queryClient,
          updatedTpl: res?.data,
          whitelist: [
            ["tci", "tpl"],
            ["tci", "tpls"],
          ],
        });
      },

      onError: (err) => {
        genErrorMsgBanners({ err });
      },
    }
  );
};

export const usePublishTpl = () => {
  const { SET_ALERT_BANNER } = useBannerContext();
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  const { SELF_MANAGED_BASE_CONFIGS } = useGetQueryData("platformConfigs");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation(
    ({ contentType }) =>
      axios.patch(
        `/api/tpl/publishTpl/${contentType}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      ),
    {
      onSuccess: (res, { contentType }) => {
        SET_ALERT_BANNER({
          color: "green",
          msg: "Template published successfully!",
          timeOut: 3000,
        });

        const segment = res.data.general.segment;

        let selfManagedRedirRoute;
        if (segment === "collections") {
          selfManagedRedirRoute =
            SELF_MANAGED_BASE_CONFIGS?.TCI?.onPublishRedirectRoute?.collections;
          selfManagedRedirRoute =
            selfManagedRedirRoute &&
            parseSpecialConfigSyntax({
              config: selfManagedRedirRoute,
              content: res.data,
            });
          navigate(selfManagedRedirRoute || `/platformBuilder/tagsManager`);
        } else {
          selfManagedRedirRoute =
            SELF_MANAGED_BASE_CONFIGS?.TCI?.onPublishRedirectRoute?.publishing;
          selfManagedRedirRoute =
            selfManagedRedirRoute &&
            parseSpecialConfigSyntax({
              config: selfManagedRedirRoute,
              content: res.data,
            });
          navigate(
            selfManagedRedirRoute ||
              `/platformBuilder/contentTypesManager?activeTplSegment=${
                segment || "uncategorized"
              }`
          );
        }

        /**
         * all caches have to be updated in this case obviously
         *
         * create/update tpl <contentType>
         * create/update tci tpl <contentType>
         *
         * update tci tpls if exists
         * update allTpls if exists BUT this always exists
         */
        updateAllTplsCache({
          queryClient,
          updatedTpl: res?.data,
        });
      },
      onError: (err) => {
        genErrorMsgBanners({ err });
      },
    }
  );
};

/**
 * In-place field component-type change (self-managed tenants only). Hits
 * PATCH /api/tpl/changeFieldComp with one standardized shape:
 *   { contentType, fieldCompChanges: [{ targetValuePath, newComp }, ...] }
 *     → BE returns { results: [{ oldTargetValuePath, newTargetValuePath, newComp,
 *       blockPath }], tpl } (applied sequentially, fail-fast)
 *
 * A single-field change is just a one-item fieldCompChanges array.
 *
 * The BE mints a fresh _vN valuePath per change and overwrites the block(s) in
 * place. The response carries the (final) `tpl`, so we sync the tpl caches from
 * res.data.tpl (NOT res.data — the payload is wrapped).
 *
 * fieldCompChanges backs the reimport sequencing: type changes on existing
 * fields fire (before the import) as one call, so no import batch is ever born
 * against a stale outline. See useReimportWithTypeChanges, which reads back
 * `results` in onSuccess and re-points its outline before firing the import.
 */
// Set on the mutation so useFieldMigrationColumnState can find in-flight /
// just-succeeded type changes in the mutation cache and derive the columns'
// "Updating data…" state from them.
export const CHANGE_FIELD_COMP_MUTATION_KEY = "changeFieldCompMutation";

export const useChangeFieldComp = () => {
  const queryClient = useQueryClient();
  const { SET_ALERT_BANNER } = useBannerContext();
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  return useMutation(
    ({ contentType, fieldCompChanges, importWins }) =>
      axios.patch(
        `/api/tpl/changeFieldComp`,
        { contentType, fieldCompChanges, importWins },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      ),
    {
      mutationKey: CHANGE_FIELD_COMP_MUTATION_KEY,
      onSuccess: (res, { fieldCompChanges }) => {
        const changedCount = Array.isArray(fieldCompChanges)
          ? fieldCompChanges.length
          : 1;
        SET_ALERT_BANNER({
          color: "green",
          msg:
            changedCount > 1
              ? `${changedCount} field types changed successfully!`
              : "Field type changed successfully!",
          timeOut: 3000,
        });
        updateAllTplsCache({
          queryClient,
          updatedTpl: res?.data?.tpl,
        });
      },
      onError: (err) => {
        genErrorMsgBanners({ err });
      },
    }
  );
};

export const useRevertTpl = () => {
  const { SET_ALERT_BANNER } = useBannerContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  const { SELF_MANAGED_BASE_CONFIGS } = useGetQueryData("platformConfigs");
  return useMutation(
    ({ contentType }) =>
      axios.patch(
        `/api/tpl/revertTplChanges/${contentType}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      ),
    {
      onSuccess: (res, { contentType }) => {
        SET_ALERT_BANNER({
          color: "green",
          msg: "Template reverted successfully!",
          timeOut: 3000,
        });

        let selfManagedRedirRoute;
        if (res?.data?.general?.segment === "collections") {
          selfManagedRedirRoute =
            SELF_MANAGED_BASE_CONFIGS?.TCI?.onRevertRedirectRoute?.collections;
          selfManagedRedirRoute =
            selfManagedRedirRoute &&
            parseSpecialConfigSyntax({
              config: selfManagedRedirRoute,
              content: res.data,
            });
          navigate(selfManagedRedirRoute || `/platformBuilder/tagsManager`);
        } else {
          selfManagedRedirRoute =
            SELF_MANAGED_BASE_CONFIGS?.TCI?.onRevertRedirectRoute.publishing;
          selfManagedRedirRoute =
            selfManagedRedirRoute &&
            parseSpecialConfigSyntax({
              config: selfManagedRedirRoute,
              content: res.data,
            });
          navigate(
            selfManagedRedirRoute ||
              `/platformBuilder/contentTypesManager?activeTplSegment=${
                res?.data?.general?.segment || "uncategorized"
              }`
          );
        }

        /**
         * since savetplchanges updates the tci caches,
         * it only makes sense that revert should revert those
         * updates in the caches
         */
        updateAllTplsCache({
          queryClient,
          updatedTpl: res?.data,
          whitelist: [
            ["tci", "tpls"],
            ["tci", "tpl"],
          ],
        });
      },

      onError: (err) => {
        genErrorMsgBanners({ err });
      },
    }
  );
};

export const useDeleteTpl = () => {
  const { SET_ALERT_BANNER } = useBannerContext();
  const queryClient = useQueryClient();
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  return useMutation(
    ({ contentType }) => axios.delete(`/api/tpl/deleteTpl/${contentType}`),

    {
      onSuccess: (res, { contentType }) => {
        SET_ALERT_BANNER({
          color: "green",
          msg: `${contentType} template deleted successfully!`,
          timeOut: 3000,
        });
        /**
         * delete tpl so:
         *
         * invalidateQueries will skip keys that dont exist
         * but freshen up the ones that do.
         *
         * and remove querues is self explanatory
         */
        queryClient.invalidateQueries(["tci", "tpls"]);
        queryClient.invalidateQueries(["allTpls"]);
        queryClient.removeQueries(["tpl", contentType]);
        queryClient.removeQueries(["tci", "tpl", contentType]);
      },

      onError: (err) => {
        genErrorMsgBanners({ err });
      },
    }
  );
};

